/**
 * Service: Tenant card backgrounds
 *
 * Background images (leaves, patterns, watermarks) that sit BEHIND the avatar
 * on a card. Tenant admins upload them; consumers pick one (or "No background").
 *
 * Storage: image files live in the existing public `card-templates` bucket
 * under `backgrounds/{tenantId}/...` (workspace policy blocks creating a
 * dedicated public bucket).
 */
import { supabase } from "@/integrations/supabase/client";

export interface CardBackgroundRow {
  id: string;
  tenant_id: string;
  name: string;
  image_url: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const BUCKET = "card-templates";

export async function listBackgrounds(tenantId: string, activeOnly = true) {
  let q = supabase
    .from("tenant_card_backgrounds")
    .select("*")
    .eq("tenant_id", tenantId)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });
  if (activeOnly) q = q.eq("is_active", true);
  return q;
}

export async function createBackground(
  tenantId: string,
  name: string,
  imageUrl: string,
  sortOrder = 0
) {
  return supabase
    .from("tenant_card_backgrounds")
    .insert({ tenant_id: tenantId, name, image_url: imageUrl, sort_order: sortOrder } as any)
    .select("*")
    .single();
}

export async function updateBackground(
  id: string,
  fields: Partial<Pick<CardBackgroundRow, "name" | "image_url" | "sort_order" | "is_active">>
) {
  return supabase
    .from("tenant_card_backgrounds")
    .update(fields as any)
    .eq("id", id)
    .select("*")
    .single();
}

export async function deleteBackground(id: string) {
  return supabase.from("tenant_card_backgrounds").delete().eq("id", id);
}

export async function uploadBackgroundImage(tenantId: string, file: File): Promise<{ url: string; error?: string }> {
  const ext = (file.name.split(".").pop() || "png").toLowerCase();
  const path = `backgrounds/${tenantId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
    cacheControl: "3600",
    upsert: false,
    contentType: file.type || undefined,
  });
  if (error) return { url: "", error: error.message };
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
  return { url: data.publicUrl };
}
